import {
  ARRAY_FOR,
  GeoIndex,
  GeoNode,
  GeoTree,
  Level,
  LEVEL_LABEL_PLURAL,
  LEVELS,
  nodeKey,
  Redundancy,
  ResultSection,
  SearchEntry,
} from '../../models/geography-tree.model';

/** Results kept per section. Capping globally would let 900 location matches bury the two shops the user wanted. */
export const PER_SECTION_CAP = 10;

/**
 * Turns the five flat arrays into the lookups every later operation needs. Roughly 6,000 operations
 * on the full tree, so it runs synchronously after fetch and is cached for the session.
 */
export function buildIndex(tree: GeoTree): GeoIndex {
  const byKey = new Map<string, { node: GeoNode; level: Level }>();
  const parentOf = new Map<string, string | null>();
  const childrenOf = new Map<string, string[]>();
  const keysByLevel = new Map<Level, string[]>();
  const searchable: SearchEntry[] = [];

  LEVELS.forEach((level, depth) => {
    const parentLevel = depth === 0 ? null : LEVELS[depth - 1];
    const keys: string[] = [];

    for (const node of tree[ARRAY_FOR[level]] ?? []) {
      const key = nodeKey(level, node.code);
      byKey.set(key, { node, level });
      keys.push(key);

      const parent = parentLevel && node.parentCode ? nodeKey(parentLevel, node.parentCode) : null;
      parentOf.set(key, parent);

      if (parent) {
        const siblings = childrenOf.get(parent);
        if (siblings) siblings.push(key);
        else childrenOf.set(parent, [key]);
      }

      searchable.push({
        key,
        level,
        code: node.code,
        name: node.name,
        haystack: `${node.code} ${node.name}`.toLowerCase(),
      });
    }

    keysByLevel.set(level, keys);
  });

  // Ancestor chains, nearest first.
  const ancestorsOf = new Map<string, string[]>();
  for (const key of byKey.keys()) {
    const chain: string[] = [];
    let cursor = parentOf.get(key) ?? null;
    while (cursor) {
      chain.push(cursor);
      cursor = parentOf.get(cursor) ?? null;
    }
    ancestorsOf.set(key, chain);
  }

  // Seed at the shops, then fold each one up its own chain.
  const shopsUnder = new Map<string, string[]>();
  for (const shop of tree.shops ?? []) {
    const key = nodeKey('shop', shop.code);
    shopsUnder.set(key, [shop.code]);

    for (const ancestor of ancestorsOf.get(key) ?? []) {
      const bucket = shopsUnder.get(ancestor);
      if (bucket) bucket.push(shop.code);
      else shopsUnder.set(ancestor, [shop.code]);
    }
  }

  return {
    version: tree.version,
    scope: tree.scope,
    byKey,
    parentOf,
    childrenOf,
    ancestorsOf,
    shopsUnder,
    keysByLevel,
    searchable,
  };
}

/**
 * The control's committed value: selection state is a set of node keys, and the emitted value is the
 * shops those keys cover. Overlapping selections collapse for free, so resolution stays correct even
 * when the user ignores every redundancy warning.
 */
export function resolveToShops(index: GeoIndex, selection: Iterable<string>): string[] {
  const shops = new Set<string>();

  for (const key of selection) {
    for (const shopCode of index.shopsUnder.get(key) ?? []) {
      shops.add(shopCode);
    }
  }

  return [...shops].sort((a, b) => a.localeCompare(b));
}

/**
 * Because the control is uncontrolled across levels, a user can pick a region and then a shop inside
 * it. That is not an error — it just doesn't do anything. Every check is a lookup over an ancestor
 * chain of at most four entries.
 */
export function checkRedundancy(index: GeoIndex, selection: ReadonlySet<string>, incoming: string): Redundancy {
  if (selection.has(incoming)) {
    return { kind: 'already-selected', incoming };
  }

  // An ancestor of the new pick is already selected → the pick adds nothing.
  const by = coveringAncestor(index, selection, incoming);
  if (by) {
    return { kind: 'covered-by', incoming, by };
  }

  // The new pick is an ancestor of things already selected → it absorbs them.
  const replaces = [...selection].filter((selected) => (index.ancestorsOf.get(selected) ?? []).includes(incoming));
  if (replaces.length > 0) {
    return { kind: 'supersedes', incoming, replaces };
  }

  return null;
}

/**
 * The nearest already-selected ancestor of `key`, or `null`. Recompute this for every remaining
 * selection after any mutation rather than storing a stale flag on each chip — the selection set is
 * small enough that a full pass costs nothing.
 */
export function coveringAncestor(index: GeoIndex, selection: ReadonlySet<string>, key: string): string | null {
  return (index.ancestorsOf.get(key) ?? []).find((ancestor) => selection.has(ancestor)) ?? null;
}

/** Lower is better. `-1` means no match. */
function rank(entry: SearchEntry, query: string): number {
  const code = entry.code.toLowerCase();
  const name = entry.name.toLowerCase();

  if (code === query) return 0; // exact code — always first
  if (name === query) return 1; // exact name
  if (code.startsWith(query)) return 2;
  if (name.startsWith(query)) return 3;
  if (entry.haystack.includes(query)) return 4;
  return -1;
}

/**
 * Matches code and name at every level at once and splits the hits into a section per level.
 * A full scan is ~2,800 entries for an unrestricted user — well under a frame, so there is no
 * debounce, no loading state and no request.
 */
export function searchGrouped(index: GeoIndex, query: string): ResultSection[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [];

  const buckets = new Map<Level, { entry: SearchEntry; rank: number }[]>();

  for (const entry of index.searchable) {
    const r = rank(entry, q);
    if (r < 0) continue;

    const bucket = buckets.get(entry.level);
    if (bucket) bucket.push({ entry, rank: r });
    else buckets.set(entry.level, [{ entry, rank: r }]);
  }

  // LEVELS order, not score order — sections stay where the user expects them.
  return LEVELS.flatMap((level) => {
    const bucket = buckets.get(level);
    if (!bucket) return [];

    bucket.sort((a, b) => a.rank - b.rank || a.entry.name.localeCompare(b.entry.name));

    return [
      {
        level,
        label: LEVEL_LABEL_PLURAL[level],
        results: bucket.slice(0, PER_SECTION_CAP).map((b) => b.entry),
        matchCount: bucket.length,
      },
    ];
  });
}

/**
 * `"Singapore · Malaysia Cluster · Asia Pacific"` — nearest ancestor first.
 *
 * Shop and location names repeat heavily across the network, so the ancestor path is what makes a
 * row unambiguous. It is required on every result, including regions where it is empty.
 */
export function ancestorPath(index: GeoIndex, key: string): string {
  return (index.ancestorsOf.get(key) ?? [])
    .map((ancestor) => index.byKey.get(ancestor)?.node.name)
    .filter(Boolean)
    .join(' · ');
}

/**
 * A saved selection can reference a node that no longer exists — a shop deactivated, a country moved
 * to another cluster. Drop the missing keys and tell the user which ones went; dropping them
 * silently changes what a saved report returns without anyone noticing.
 */
export function reconcileSelection(index: GeoIndex, selection: readonly string[]): { kept: string[]; dropped: string[] } {
  const kept: string[] = [];
  const dropped: string[] = [];

  for (const key of selection) {
    if (index.byKey.has(key)) kept.push(key);
    else dropped.push(key);
  }

  return { kept, dropped };
}
